import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';

import { ApplicationFormFieldComponent } from '../../../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { forkJoin, takeUntil, tap, timer } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { DropdownService } from '../../../../../../core/services/resources/dropdown.service';
import { MatButtonModule } from '@angular/material/button';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { ScheduleDStepTwoService } from './services/schedule-d-step-two/schedule-d-step-two.service';
import { ScheduleDStepTwoHelperMessages } from './constants/schedule-d-step-two-helper-messages';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { LatitudeLongitudeDms } from './models/latitude-longitude-dms';
import { GeoCoordinateSystemService } from '../../../../../../core/services/geo-coordinate-system/geo-coordinate-system.service';
import { Direction } from '../../../../../../core/models/direction';
import { Parallel } from '../../../../../../core/models/parallel';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import { Geography } from '../../../../../../core/constants/geography';
import { ScheduleDStepBaseComponent } from '../schedule-d-step-base/schedule-d-step-base.component';
import { ValidationResult } from '../../../../../../core/services/validation/models/validation-result';
import { IMaskModule } from 'angular-imask';
import { ScheduleDStepTwoMaskConfig } from './models/schedule-d-step-two-mask-config';
import { ScheduleDFormModel } from '../../models/schedule-d-form-model';
import { ScheduleDFormService } from '../../services/schedule-d-form/schedule-d-form.service';
import { ScheduleDLocationType } from '../../types/schedule-d-location-type';
import { LatitudeLongitudeMaskConfig } from '../../../../shared/components/latitude-longitude-form/constants/latitude-longitude-mask-config';
import { SchedulesDAndHMaskConfig } from '../../../../shared/constants/schedules-d-and-h-mask-config';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { LatitudeLongitudeFormComponent } from '../../../../shared/components/latitude-longitude-form/latitude-longitude-form.component';
import { PopulateScheduleDAsrDataService } from '../../services/schedule-d-form/populate-schedule-d-asr-data.service';
import { SelectTypeaheadDirective } from '../../../../../../shared/directives/select-typeahead/select-typeahead.directive';
import { ActionCode } from '../../../../../../core/models/resources/action-code';

@Component({
    selector: 'app-schedule-d-step-two',
    imports: [
        MatSelectModule,
        ApplicationFormFieldComponent,
        MatFormFieldModule,
        FormsModule,
        MatInputModule,
        MatButtonModule,
        ReactiveFormsModule,
        RegexInputDirective,
        MatIconModule,
        MatCheckboxModule,
        IMaskModule,
        LatitudeLongitudeFormComponent,
        MatAutocompleteModule,
        SelectTypeaheadDirective,
    ],
    templateUrl: './schedule-d-step-two.component.html',
})
export class ScheduleDStepTwoComponent
    extends ScheduleDStepBaseComponent
    implements OnInit, AfterViewInit
{
    private readonly dropdownService = inject(DropdownService);
    private readonly stepService = inject(ScheduleDStepTwoService);
    private readonly coordinatesService = inject(GeoCoordinateSystemService);
    private readonly formService = inject(ScheduleDFormService);
    private readonly applicationFormControlManager = inject(
        ApplicationFormControlManagerService,
    );
    private readonly populateScheduleDAsrDataService = inject(
        PopulateScheduleDAsrDataService,
    );

    @Input() scheduleDs!: FormArray<
        FormGroup<FormGroupType<ScheduleDFormModel>>
    >;

    // Static is false because the inputElement is conditionally rendered

    @ViewChild('correspondingFixedLocationField', { static: false })
    correspondingFixedLocationField: MatSelect | undefined;

    @ViewChild('stateField', { static: false }) stateField:
        MatSelect | undefined;

    @ViewChild('areaOfOperationTextField', { static: false })
    areaOfOperationTextField: ElementRef | undefined;

    @ViewChild('toggleLatitudeLongitudeFormatElement', { static: false })
    toggleLatitudeLongitudeFormatElement: MatCheckbox | undefined;

    @ViewChild('cityField', { static: false }) cityField:
        ElementRef | undefined;

    // TODO: remove for reusable lat/lng component in future tech debt ticket
    /*
     * Form group for the latitude and longitude in DMS (Degree/Minutes/Seconds) format.
     * Form value will be converted to decimal format and set to the main form.
     */
    dmsForm: FormGroup<FormGroupType<LatitudeLongitudeDms>> = new FormGroup<
        FormGroupType<LatitudeLongitudeDms>
    >({
        latitudeDegrees: new FormControl(null, [
            Validators.required,
            Validators.min(Geography.latitudeLongitudeDegreesMinimum),
            Validators.max(Geography.latitudeUnitedStatesAndTerritoriesMaximum),
        ]),
        latitudeMinutes: new FormControl(null, [
            Validators.required,
            Validators.min(Geography.latitudeLongitudeMinutesMinimum),
            Validators.max(Geography.latitudeLongitudeMinutesMaximum),
        ]),
        latitudeSeconds: new FormControl(null, [
            Validators.required,
            Validators.min(Geography.latitudeLongitudeSecondsMinimum),
            Validators.max(Geography.latitudeLongitudeSecondsMaximum),
        ]),
        latitudeDirection: new FormControl(Direction.North, [
            Validators.required,
        ]),
        longitudeDegrees: new FormControl(null, [
            Validators.required,
            Validators.min(Geography.latitudeLongitudeDegreesMinimum),
            Validators.max(
                Geography.longitudeUnitedStatesAndTerritoriesMaximum,
            ),
        ]),
        longitudeMinutes: new FormControl(null, [
            Validators.required,
            Validators.min(Geography.latitudeLongitudeMinutesMinimum),
            Validators.max(Geography.latitudeLongitudeMinutesMaximum),
        ]),
        longitudeSeconds: new FormControl(null, [
            Validators.required,
            Validators.min(Geography.latitudeLongitudeSecondsMinimum),
            Validators.max(Geography.latitudeLongitudeSecondsMaximum),
        ]),
        longitudeDirection: new FormControl(Direction.West, [
            Validators.required,
        ]),
    });

    latitudeLongitudeInDecimalFormat: boolean = false;
    coordinateIsInUSOrTerritoriesResult: ValidationResult | null = null;

    messages = ScheduleDStepTwoHelperMessages;

    correspondingFixedLocationOptions: DropdownOption[] = [];
    stateOptions: DropdownOption[] = [];
    latitudeDirectionOptions: DropdownOption[] = [];
    longitudeDirectionOptions: DropdownOption[] = [];
    structureTypeOptions: DropdownOption[] = [];

    latitudeLongitudeMaskConfig = LatitudeLongitudeMaskConfig;
    schedulesDAndHMaskConfig = SchedulesDAndHMaskConfig;
    scheduleDStepTwoMaskConfig = ScheduleDStepTwoMaskConfig;
    regExPattern = RegExPattern;

    constructor() {
        super();
    }

    get locationIsSavedToApplicationOrIsModification(): boolean {
        return (
            this.form.controls.id.value !== null ||
            this.form.controls.actionCode.value !== ActionCode.Add
        );
    }

    ngOnInit(): void {
        this.loadDropdownData();
        this.setupReadOnly();

        this.synchronizeFixedLocationOptionsWithScheduleD();
        this.initializeCorrespondingFixedLocationOptions();

        this.conditionallyLoadPrepopulatedDataFromASRNumber();

        // Initialize the dms form if latitude and longitude have values.
        // If this wasn't done, the DMS values would be null when lat/lng have values and user navigates back a step then forward.
        this.updateDmsLatitudeLongitudeValues('latitude');
        this.updateDmsLatitudeLongitudeValues('longitude');
    }

    ngAfterViewInit(): void {
        const locationType: ScheduleDLocationType | null =
            this.stepService.getLocationType(this.form);

        // Set the initial focus based on the location type.
        setTimeout(() => {
            switch (locationType) {
                case 'A':
                    this.correspondingFixedLocationField?.focus();
                    break;
                case 'C':
                case 'S':
                case 'X':
                    this.stateField?.focus();
                    break;
                case 'O':
                    this.areaOfOperationTextField?.nativeElement.focus();
                    break;
                case 'P':
                case 'fixed':
                    this.toggleLatitudeLongitudeFormatElement?.focus();
                    break;
                case 'T':
                    this.cityField?.nativeElement.focus();
                    break;
            }
        });
    }

    isReadOnly(): boolean {
        return this.applicationFormControlManager.isReadOnly();
    }

    setupReadOnly(): void {
        if (this.isReadOnly()) {
            this.applicationFormControlManager.disable(this.dmsForm, {
                emitEvent: false,
            });
        }
    }

    /**
     * Returns whether the form should show a field based on the type of schedule d.
     * @param field the field to check.
     * @returns true if the form should show the field, otherwise false.
     */
    canHaveField(field: keyof ScheduleDFormModel): boolean {
        return this.stepService.canHaveField(this.form, field);
    }

    toggleLatitudeLongitudeFormat(): void {
        this.latitudeLongitudeInDecimalFormat =
            !this.latitudeLongitudeInDecimalFormat;

        // Timer is used to allow the form controls to render before marking them.
        timer(0).subscribe(() => {
            this.markFormControlsFromLatitudeLongitudeValidationResult();
        });
    }

    private initializeCorrespondingFixedLocationOptions(): void {
        this.correspondingFixedLocationOptions =
            this.getCorrespondingFixedLocationOptions(this.scheduleDs);
    }

    /**
     * When new locations get added to the list, we want to know about them so we can add them
     * to the list of fixed location numbers to choose from if you are adding a
     * KMRA Around a Fixed Location type.
     */
    private synchronizeFixedLocationOptionsWithScheduleD(): void {
        (this.scheduleDs as FormArray).valueChanges.pipe(
            takeUntil(this.destroy$),
            tap((value: FormArray) => {
                this.correspondingFixedLocationOptions =
                    this.getCorrespondingFixedLocationOptions(value);
            }),
        );
    }

    // TODO: remove for reusable lat/lng component in future tech debt ticket
    private updateDmsLatitudeLongitudeValues(parallel: Parallel) {
        if (this.form.controls[parallel].value == null) {
            return;
        }

        const control = this.form.controls[parallel];
        const dms = this.coordinatesService.convertDecimalToDMS(
            Number(control.value),
            parallel,
        );

        this.dmsForm.patchValue(
            {
                [nameOf<LatitudeLongitudeDms>(`${parallel}Degrees`)]:
                    dms.degrees,
                [nameOf<LatitudeLongitudeDms>(`${parallel}Minutes`)]:
                    dms.minutes,
                [nameOf<LatitudeLongitudeDms>(`${parallel}Seconds`)]:
                    dms.seconds,
                [nameOf<LatitudeLongitudeDms>(`${parallel}Direction`)]:
                    dms.direction,
            },
            { emitEvent: false },
        );
    }

    /**
     * Marks the latitude and longitude controls as invalid if the coordinate is not in the US or territories.
     * If the coordinate is valid, the errors are cleared.
     * @returns void
     */
    private markFormControlsFromLatitudeLongitudeValidationResult(): void {
        if (!this.coordinateIsInUSOrTerritoriesResult) {
            return;
        }

        if (this.coordinateIsInUSOrTerritoriesResult.isValid === false) {
            this.bulkSetLatitudeLongitudeErrorsAndMarkAsTouched({
                invalidCoordinate: true,
            });
        } else {
            this.bulkSetLatitudeLongitudeErrorsAndMarkAsTouched(null);
        }
    }

    // TODO: remove for reusable lat/lng component in future tech debt ticket
    /**
     * Sets errors on all the latitude and longitude controls (including the DMS form) and marks them as touched.
     * @param errors the errors to set on each control
     */
    private bulkSetLatitudeLongitudeErrorsAndMarkAsTouched(
        errors: { [key: string]: boolean } | null,
    ) {
        this.form.controls.latitude.setErrors(errors);
        this.form.controls.longitude.setErrors(errors);
        this.form.controls.latitude.markAsTouched();
        this.form.controls.longitude.markAsTouched();

        this.dmsForm.controls.latitudeDegrees.setErrors(errors);
        this.dmsForm.controls.latitudeMinutes.setErrors(errors);
        this.dmsForm.controls.latitudeSeconds.setErrors(errors);
        this.dmsForm.controls.latitudeDirection.setErrors(errors);

        this.dmsForm.controls.longitudeDegrees.setErrors(errors);
        this.dmsForm.controls.longitudeMinutes.setErrors(errors);
        this.dmsForm.controls.longitudeSeconds.setErrors(errors);
        this.dmsForm.controls.longitudeDirection.setErrors(errors);

        this.dmsForm.markAllAsTouched();
    }

    private loadDropdownData() {
        forkJoin({
            stateOptions: this.dropdownService.getStateCodes(),
            latitudeDirectionOptions:
                this.dropdownService.getLatitudeDirectionOptions(),
            longitudeDirectionOptions:
                this.dropdownService.getLongitudeDirectionOptions(),
            structureTypeOptions: this.dropdownService.getStructureTypeCodes(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(
                    ({
                        stateOptions: stateOptions,
                        latitudeDirectionOptions: latitudeDirectionOptions,
                        longitudeDirectionOptions: longitudeDirectionOptions,
                        structureTypeOptions: structureTypeOptions,
                    }) => {
                        this.stateOptions = stateOptions;
                        this.latitudeDirectionOptions =
                            latitudeDirectionOptions;
                        this.longitudeDirectionOptions =
                            longitudeDirectionOptions;
                        this.structureTypeOptions = structureTypeOptions;
                    },
                ),
            )
            .subscribe();
    }

    /**
     * Returns the corresponding fixed location options for the given schedule d values.
     * The label includes the location number and (if available) location name.
     */
    private getCorrespondingFixedLocationOptions(
        scheduleDs: FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>,
    ): DropdownOption[] {
        const options = scheduleDs.controls
            .filter(
                (control) =>
                    this.formService.isFixed(
                        control as FormGroup<FormGroupType<ScheduleDFormModel>>,
                    ) && control.value.number !== null,
            )
            .map((control) => {
                const labelItems = [control.value.number?.toString()];
                if (control.value.locationName) {
                    labelItems.push(control.value.locationName);
                }

                return {
                    value: control.value.number,
                    label: labelItems.join(' - '),
                } as DropdownOption;
            });

        return this.dropdownService.prefixDropdownOptionsWithEmpty(options);
    }

    private conditionallyLoadPrepopulatedDataFromASRNumber(): void {
        if (
            this.form.controls.towerRegistrationNumber.value &&
            this.form.controls.towerRegistrationNumber.dirty
        ) {
            this.populateScheduleDAsrDataService.getAsrCoordinateData(
                this.form,
            );
        }
    }
}
